<script setup lang="ts">
import { Codemirror, DEFAULT_CONFIG } from 'vue-codemirror';
import CopyButton from '@/common/components/molecules/copyButton/CopyButton.vue';
import SkeletonLoading from '@/common/components/molecules/skeletonLoading/SkeletonLoading.vue';
import type { Props, Emits } from './codeEditor.setup';
import { setup, useCodeMirror } from './codeEditor.setup';

const props = withDefaults(defineProps<Props>(), {
  options: () =>
    useCodeMirror({
      theme: 'light',
      options: DEFAULT_CONFIG,
    }).codeEditorOptions,
  placeholder: '',
});

const emits = defineEmits<Emits>();
const {
  t,
  codeText,
  wrapperRef,
  loadElHeight,
  loadTargetLength,
  handleReady,
  lineHighlight,
  getFocusedLine,
  scrollInto,
} = setup(props, emits);

defineExpose({
  lineHighlight,
  getFocusedLine,
  scrollInto,
});
</script>

<template>
  <div
    ref="wrapperRef"
    class="code-editor-wrapper"
  >
    <copy-button
      v-if="props.options?.useCopy && !isLoading"
      class="code-editor__copy-button"
      :title="t('WORD.COPY', 'Copy')"
      @click="emits('copy', codeText)"
    />
    <codemirror
      ref="view"
      v-model="codeText"
      :class="[
        'code-editor',
        { 'code-editor--disable-line-number': !props.options.showLineNumbers },
        { 'code-editor--disable-active-current-line': !props.options.showActiveCurrentLine },
      ]"
      v-bind="props.options"
      :placeholder="props.placeholder"
      @change="(value) => emits('change', value)"
      @ready="handleReady"
    />
    <div
      v-if="isLoading"
      class="load-area"
    >
      <template
        v-for="index in loadTargetLength"
        :key="index"
      >
        <skeleton-loading :height="`${loadElHeight - 6}px`" />
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.code-editor-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}
.custom-stat-script-test__codemirror {
  .cm-gutters {
    display: none;
  }

  .cm-content {
    flex-grow: unset !important;
    flex-shrink: unset !important;
    word-break: break-all;
  }

  .cm-line {
    display: block !important;
    flex-grow: unset !important;
    flex-shrink: unset !important;
    white-space: pre-wrap !important;
    word-break: break-all;
  }

  .cm-line * {
    flex-grow: unset !important;
    flex-shrink: unset !important;
    white-space: pre-wrap !important;
    word-break: break-all;
  }
}
.code-editor {
  &__copy-button {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
  }
  &--disable-line-number {
    .cm-gutter.cm-lineNumbers {
      display: none;
    }
  }

  &--disable-active-current-line {
    .cm-activeLine,
    .cm-activeLineGutter {
      background: none;
    }

    .cm-content {
      flex-grow: unset;
      flex-shrink: unset;
    }
  }

  .cm-selectionMatch {
    background-color: #99ff77;
    color: var(--color-gray-12-12);
  }

  .cm-panels {
    z-index: 99;
  }

  .cm-focused {
    outline: none;
  }

  [data-language='yaml'] {
    .cm-line {
      color: var(--main-font-color);
    }
  }
  .line-highlight {
    background-color: var(--color-yellow-02);
  }
  .cm-lintRange-error {
    background-image: url('@/common/assets/fake-wave-line.svg');
  }
  .cm-gutter-lint {
    order: -1;
    width: 6px;
    .cm-lint-marker {
      width: 6px;
      height: 24px;
      transform: translateY(-6px);

      &-error {
        border-left: 0.5rem solid var(--color-red-06);
        content: none;
      }
    }
  }
  .cm-placeholder {
    font-family: $font-family-base;

    @include typography('body-2');
  }
}
.load-area {
  $load-left: 42px;
  $load-gap: 6px;

  display: flex;
  position: absolute;
  top: $load-gap;
  left: $load-left;
  flex-direction: column;
  gap: $load-gap;
  width: calc(100% - #{$load-left});
}
</style>
